import { useLocation } from "react-router-dom"

import type { Route } from "@/api/models"

import { useRoutes } from "@/contexts/use-routes"

/**
 * Determines the current session type from the active route.
 * Uses longest path matching to handle nested routes correctly.
 *
 * This hook should only be used inside components wrapped by RouteReadyGuard,
 * which ensures routes are loaded before rendering.
 *
 * @throws Error if routes aren't loaded or no matching route is found
 * @returns The session type (route ID) for the current path
 *
 * @example
 * // On /custom-variant/chat/123
 * const sessionType = useCurrentSessionType() // Returns "custom-variant"
 *
 * // On /ai-parser
 * const sessionType = useCurrentSessionType() // Returns "ai-parser"
 */
export function useCurrentSessionType(): string {
  const { routes } = useRoutes()
  const location = useLocation()

  // This should never happen if RouteReadyGuard is properly used
  if (routes.length === 0) {
    throw new Error("Routes not loaded - component must be wrapped in RouteReadyGuard")
  }

  const sessionType = resolveSessionType(routes, location.pathname)
  if (sessionType) return sessionType

  throw new Error(
    `No route found for path: ${location.pathname}. This indicates a routing configuration error.`
  )
}

/**
 * Like {@link useCurrentSessionType}, but returns null instead of throwing when
 * routes are not loaded or no route matches. For components that can render
 * outside a module route, such as the canvas in the app-wide side panel.
 */
export function useOptionalSessionType(): string | null {
  const { routes } = useRoutes()
  const location = useLocation()
  if (routes.length === 0) return null
  return resolveSessionType(routes, location.pathname)
}

function resolveSessionType(routes: Route[], pathname: string): string | null {
  const currentRoute = routes
    .filter((route) => pathname.startsWith(route.path))
    .sort((a, b) => b.path.length - a.path.length)
    .at(0)

  if (currentRoute) {
    const overrideSessionType = currentRoute.overrides?.overrideSessionType
    if (typeof overrideSessionType === "string" && overrideSessionType.trim()) {
      return overrideSessionType
    }
    return currentRoute.id
  }

  // Built-in /documents/* routes are intentionally not in the enabled routes
  // list (they aren't a module), but shared sidebar hooks still need a session
  // type. Fall back to the primary explorer route, matching the
  // overrideSessionType wired up in routes-context for the documents metadata.
  if (pathname.startsWith("/documents")) {
    const primaryExplorer = routes.find((r) => r.component === "free")
    if (primaryExplorer) return primaryExplorer.id
    return "open"
  }

  return null
}
